import { Collapse, Divider, Icon, TextField, Tooltip } from '@mui/material'
import {
  SavedFilter,
  useCreateSavedFilter,
  useDeleteSavedFilter,
  useSavedFilters,
} from 'api/savedFilters/savedFilters.api'
import { getRoute } from 'helpers/route.helper/route.helper'
import { useApplySavedFilter } from 'hooks/filterHooks/useApplySavedFilter'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { useMainRoutes } from 'routes/main.routes'
import alertStore from 'store/alert/alert'
import filterStore, { initGroupingPerRoute, setGrouping } from 'store/filter/filter'
import {
  FILTER_QUERYSTRING_PARAM,
  GROUPING_QUERYSTRING_PARAM,
  normalizeFilterQueryString,
} from 'store/filter/urlFilter.helper'
import { colors } from 'styles/variables'
import NewButton from 'ui/atoms/NewButton/NewButton'
import NewIcon from 'ui/atoms/NewIcon/NewIcon'
import sentry from 'utils/sentry/sentry'
import { useSnapshot } from 'valtio'
import { useAuth } from '../AuthContext/AuthContext'

type SavedFilterDropdownProps = {
  isActiveTagsFiltered?: boolean
}

export default function SavedFilterDropdown({ isActiveTagsFiltered }: SavedFilterDropdownProps): React.JSX.Element {
  const { t } = useTranslation()
  const { systemId } = useAuth()
  const mainRoutes = useMainRoutes()
  const location = useLocation()

  // Assumption that default grouping for optimize is the default grouping for the whole app
  const DEFAULT_GROUPING = initGroupingPerRoute['optimize']

  //Returns the name of the current module based on the current path, if not found, returns the path without leading slash
  const currentModule = useMemo(() => {
    const findModuleInRoutes = (routes: any[]): string | undefined => {
      for (const route of routes) {
        // Check if current route matches
        if (route.path === location.pathname && route.module) {
          return route.module
        }
        // If route has children, search recursively
        if (route.children) {
          const childModule = findModuleInRoutes(route.children)
          if (childModule) return childModule
        }
      }
      return undefined
    }

    const foundModule = findModuleInRoutes(mainRoutes)

    return foundModule || location.pathname.slice(1) //remove leading slash
  }, [mainRoutes, location.pathname])

  const route = getRoute()
  const navigate = useNavigate()
  const applySavedFilter = useApplySavedFilter()

  const { groupingPerRoute } = useSnapshot(filterStore)
  const { data: savedFiltersData, isLoading, error } = useSavedFilters(systemId?.toString())
  const createFilterMutation = useCreateSavedFilter()
  const deleteFilterMutation = useDeleteSavedFilter()
  const dropdownRef = useRef<HTMLDivElement>(null)
  const routeHasGrouping = Object.keys(groupingPerRoute).some((key) => key.includes(route))

  const [expanded, setExpanded] = useState<boolean>(false)

  const filters: SavedFilter[] = Array.isArray(savedFiltersData) ? savedFiltersData : []

  const handleAddFilter = async (name: string, filter: string) => {
    try {
      // The value comes straight off the URL, which spells the blob in either base64 alphabet
      // and carries spaces where `URLSearchParams` decoded an unencoded `+`.
      const canonicalFilter = normalizeFilterQueryString(filter)

      await createFilterMutation.mutateAsync({
        name,
        filter: canonicalFilter,
        module: currentModule,
        systemId: systemId?.toString(),
      })

      // Set grouping to default if route has no grouping to make it look selected in list (since it's created with default grouping)
      if (!routeHasGrouping) {
        setGrouping(DEFAULT_GROUPING, navigate)
      }

      // Show success message after navigation to ensure it's visible
      setTimeout(() => {
        alertStore.success(t('Filter saved'))
      }, 100)
    } catch (error) {
      alertStore.error(t('Failed to save filter'))
      sentry.captureException(error as Error, {
        level: 'warning',
        tags: {
          sentry_scope: 'saved_filter_dropdown',
          issue_type: 'save_filter_failure',
          module: currentModule,
        },
        extra: {
          systemId,
          routeHasGrouping,
        },
        fingerprint: ['saved_filter_dropdown', 'save_filter_failure', currentModule],
      })
    }
  }

  const handleDeleteFilter = async (id: number) => {
    try {
      await deleteFilterMutation.mutateAsync(id.toString())
      alertStore.success(t('Filter deleted'))
    } catch (error) {
      alertStore.error(t('Failed to delete filter'))
      sentry.captureException(error as Error, {
        level: 'warning',
        tags: {
          sentry_scope: 'saved_filter_dropdown',
          issue_type: 'delete_filter_failure',
          module: currentModule,
        },
        extra: {
          filterId: id,
          systemId,
        },
        fingerprint: ['saved_filter_dropdown', 'delete_filter_failure', currentModule],
      })
    }
  }

  const filterParams = new URLSearchParams(window.location?.search).get(FILTER_QUERYSTRING_PARAM) || ''
  const groupByFilterParams = new URLSearchParams(window.location?.search).get(GROUPING_QUERYSTRING_PARAM) || ''

  const currentFilterIncludingGroupByFilter = useMemo(() => {
    if (groupByFilterParams) {
      // Make sure to change split in onFilterClick if filterformat changes
      return filterParams ? `${filterParams}&${groupByFilterParams}` : groupByFilterParams
    }
    // If route has grouping, but no grouping is shown in URL. Set default grouping for the route.
    if (routeHasGrouping) {
      return filterParams ? `${filterParams}&${groupingPerRoute[route]}` : `${groupingPerRoute[route]}`
    }

    // If route has no grouping and no grouping is shown in URL, set default grouping for optimize

    return filterParams ? `${filterParams}&${DEFAULT_GROUPING}` : `${DEFAULT_GROUPING}`
  }, [filterParams, groupByFilterParams, routeHasGrouping, groupingPerRoute, route])

  const isFilterActive = useCallback(
    (filter: string): boolean => {
      if (currentFilterIncludingGroupByFilter === '') return false

      // Compared canonically, not as raw strings: a filter saved before the param moved to
      // the URL-safe base64 alphabet spells the same bytes with `+` and `/`, so a raw
      // comparison reads it as a different filter — leaving "Save current filter" offered
      // for a filter already saved, and the matching row unhighlighted.
      return normalizeFilterQueryString(filter) === normalizeFilterQueryString(currentFilterIncludingGroupByFilter)
    },
    [currentFilterIncludingGroupByFilter]
  )

  const isSavedFilterActive = useMemo(() => {
    // Check if current URL filter matches any saved filter
    return filters.some((filter) => isFilterActive(filter.filter))
  }, [filters, isFilterActive])

  useEffect(() => {
    if (!expanded) {
      return
    }

    function handleClickOutsideDropdown(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setExpanded(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutsideDropdown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideDropdown)
    }
  }, [expanded])

  function onFilterClick(filter: SavedFilter) {
    applySavedFilter(filter)
    setExpanded(false)
  }

  return (
    <div ref={dropdownRef} className="relative self-center">
      <div
        className={`flex flex-row items-center justify-between border-gray-300 w-62 h-10 border ${expanded ? 'rounded-t-md border-b-0' : 'rounded-md'} p-2 cursor-pointer`}
        data-testid="saved-filters-trigger" // e2e: "My saved filters" dropdown trigger — do not remove
        onClick={() => {
          setExpanded(!expanded)
        }}
      >
        <div className="mx-1 flex flex-row items-center justify-between w-full">
          <div className="flex flex-row items-center gap-3">
            <Icon fontSize="small" className={isSavedFilterActive ? 'fas fa-bookmark' : 'fal fa-bookmark'} />
            {t('My saved filters')}
          </div>
          <NewIcon icon={expanded ? 'fal fa-chevron-down' : 'fal fa-chevron-right'} color="black" ariaLabel="expand" />
        </div>
      </div>
      <Collapse in={expanded} timeout="auto">
        <div className="flex flex-col w-62 bg-white border border-gray-300 rounded-b-md border-t-0 p-2 absolute z-10">
          <div className="overflow-y-auto max-h-[20vh] [@media(min-height:640px)]:max-h-[40vh]">
            {!isLoading && !error && filters && filters.length > 0 ? (
              filters
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((filter, index) => (
                  <FilterItem
                    key={`saved-filter-dropdown-${index}`}
                    filter={filter}
                    index={index}
                    isCurrentFilterActive={isFilterActive(filter.filter)}
                    onFilterClick={() => {
                      onFilterClick(filter)
                    }}
                    onDelete={() => handleDeleteFilter(filter.id)}
                  />
                ))
            ) : (
              <div className="text-gray-500 mb-3 ml-1">{t('No saved filters found')}</div>
            )}
            <Divider />
            <CreateSavedFilter
              filters={filters}
              currentActiveFilters={
                routeHasGrouping
                  ? `${filterParams}&${groupByFilterParams ? groupByFilterParams : DEFAULT_GROUPING}`
                  : `${filterParams}&${DEFAULT_GROUPING}`
              }
              isSavedFilterActive={isSavedFilterActive}
              isActiveTagsFiltered={isActiveTagsFiltered}
              onSave={handleAddFilter}
            />
          </div>
        </div>
      </Collapse>
    </div>
  )
}

type FilterItemProps = {
  filter: any
  index: number
  isCurrentFilterActive: boolean
  onFilterClick: () => void
  onDelete: () => void
}

function FilterItem({
  filter,
  index,
  isCurrentFilterActive,
  onFilterClick,
  onDelete,
}: FilterItemProps): React.JSX.Element {
  const { t } = useTranslation()
  const labelTextRef = useRef<HTMLSpanElement>(null)
  const [isTextTruncated, setIsTextTruncated] = useState(false)

  useEffect(() => {
    if (labelTextRef.current) {
      const element = labelTextRef.current
      const isTruncated = element.scrollWidth > element.clientWidth
      setIsTextTruncated(isTruncated)
    }
  }, [filter])

  return (
    <div
      className={`flex flex-row items-center justify-between ml-1 my-1 p-1 rounded-md hover:cursor-pointer ${
        isCurrentFilterActive ? 'bg-gray-200 hover:bg-gray-300' : 'hover:bg-gray-100'
      }`}
      data-testid={`saved-filter-row-${filter.name}`} // e2e: saved-filter row keyed by name — do not remove
      onClick={onFilterClick}
    >
      <Tooltip title={isTextTruncated ? filter.name : ''} arrow placement="bottom-start">
        <span className="text-sm w-50 truncate" ref={labelTextRef}>
          {filter.name || `Saved Filter ${index + 1}`}
        </span>
      </Tooltip>
      <div className="flex flex-row items-center ml-2">
        <NewButton
          variant="delete"
          iconButton
          iconSize="small"
          label={t('Delete')}
          data-testid={`delete-saved-filter-${filter.name}`} // e2e: per-row delete keyed by name — do not remove
          onClick={(e) => {
            e.stopPropagation()
            onDelete()
          }}
        />
      </div>
    </div>
  )
}

type CreateSavedFilterProps = {
  filters: SavedFilter[]
  currentActiveFilters: string
  isSavedFilterActive: boolean
  onSave: (name: string, filter: string) => Promise<void>
  isActiveTagsFiltered?: boolean
}

function CreateSavedFilter({
  filters,
  currentActiveFilters,
  isSavedFilterActive,
  onSave,
  isActiveTagsFiltered,
}: CreateSavedFilterProps): React.JSX.Element {
  const { t } = useTranslation()

  const [isCreating, setIsCreating] = useState<boolean>(false)
  const [newFilterName, setNewFilterName] = useState<string>('')

  const existingFilterName = filters.some(
    (filter) => filter.name.toLocaleLowerCase() === newFilterName.trim().toLocaleLowerCase()
  )

  const disableSaveFilter = newFilterName.trim() === '' || existingFilterName
  const disableSaveCurrentFilter = !isActiveTagsFiltered || isSavedFilterActive

  return (
    <div
      className={`flex flex-row items-center hover:cursor-pointer mt-2 py-1 rounded-md ${disableSaveCurrentFilter ? 'cursor-default' : 'hover:bg-gray-100'}`}
    >
      {!isCreating && (
        // `aria-disabled` rather than the native `disabled` attribute: a disabled button suppresses
        // pointer events, which would also suppress the tooltip that explains *why* saving is
        // unavailable. The control stays focusable and announces its state, and the click handler
        // guards the action.
        <button
          type="button"
          className="p-1 rounded-md flex flex-row items-center justify-between bg-transparent border-0"
          key={`create-saved-filter-dropdown`}
          data-testid="save-current-filter-button" // e2e: open the save-current-filter input — do not remove
          aria-disabled={disableSaveCurrentFilter} // e2e: exposes enablement as state so it is not read from styling — do not remove
          onClick={() => {
            if (!disableSaveCurrentFilter) {
              setIsCreating(true)
            }
          }}
        >
          <Tooltip
            title={
              !isActiveTagsFiltered
                ? t('There is no current filter to be saved')
                : isSavedFilterActive
                  ? t('This filter combination is already saved')
                  : ''
            }
            arrow
            placement="bottom-start"
          >
            <div className={`${!disableSaveCurrentFilter ? '' : 'text-gray-400 cursor-default'} flex items-center `}>
              <Icon className="fal fa-plus" fontSize="small" />
              <span className="text-sm mx-2">{t('Save current filter')}</span>
            </div>
          </Tooltip>
        </button>
      )}
      {isCreating && (
        <>
          <TextField
            label={t('Name')}
            variant="outlined"
            value={newFilterName}
            size="medium"
            autoFocus
            slotProps={{ htmlInput: { 'data-testid': 'saved-filter-name-input' } }} // e2e: saved-filter name input — do not remove
            onChange={(e) => {
              setNewFilterName(e.target.value)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !disableSaveFilter) {
                onSave(newFilterName, currentActiveFilters)
                setNewFilterName('')
                setIsCreating(false)
              }
            }}
          />
          <div className="flex flex-row items-center ml-2">
            <Tooltip
              title={
                existingFilterName
                  ? t('Please enter a unique filter name')
                  : newFilterName.trim() === ''
                    ? t('Please enter a filter name')
                    : ''
              }
              arrow
            >
              <Icon
                className={`fas fa-check`}
                fontSize="small"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation() // Prevent the click from bubbling up to the parent div

                  if (!disableSaveFilter) {
                    onSave(newFilterName, currentActiveFilters)
                    setNewFilterName('')
                    setIsCreating(false)
                  }
                }}
                style={{ color: disableSaveFilter ? colors.grey4 : colors.success4 }}
              />
            </Tooltip>
          </div>
          <div
            className="flex flex-row items-center ml-2"
            onClick={() => {
              setNewFilterName('')
              setIsCreating(false)
            }}
          >
            <Icon className="fas fa-times text-red-400" fontSize="small" />
          </div>
        </>
      )}
    </div>
  )
}
